import { ChangeDetectionStrategy, Component } from '@angular/core';
import { Store } from '@ngrx/store';
import { AUTH_ACTIONS } from '../../core/store/actions/auth.actions';
import { combineLatest, Observable } from 'rxjs';
import { AUTH_SELECTORS } from '../../core/store/selectors/auth.selectors';
import { HeaderService } from '../header';
import { APPLICATION_SELECTORS } from '../../core/store/selectors/application.selector';
import { USER_PREFERENCES_REDUCER } from '../../core/store/reducers/user-preferences.reducer';
import { map } from 'rxjs/operators';
import { CLAIMS_SELECT } from '../../core/store/selectors/claims.selector';
import { CriticalErrorComponent } from '../../shared/components/critical-error/critical-error.component';
import { AsyncPipe, NgClass } from '@angular/common';
import { HeaderModule } from '../header/header.module';
import { NavigationComponent } from '../navigation/navigation.component';
import { RouterOutlet } from '@angular/router';
import { ACCOUNTS_SELECTORS } from '../../core/store/selectors/accounts.selectors';
import { SITES_SELECTORS } from '../../core/store/selectors/sites.selectors';

@Component({
  selector: 'lh-layout',
  template: `
    @if (data$ | async; as data) {
      <div class="layout">
        <lh-header [userName]="data.userName" (logout)="onLogout()"></lh-header>
        <div class="layout__container" [ngClass]="{ 'layout__container--no-main-header': data.isHeaderHidden }">
          @if (!data.isCriticalError) {
            <lh-navigation></lh-navigation>
          }

          <div class="layout__content" [ngClass]="{ 'layout__content--navigation-pinned': data.isNavigationPinned }">
            @if (!data.isCriticalError) {
              <router-outlet></router-outlet>
            } @else {
              <lh-critical-error></lh-critical-error>
            }
          </div>
        </div>
      </div>
    }
  `,
  styleUrls: ['./layout.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CriticalErrorComponent, AsyncPipe, HeaderModule, NgClass, NavigationComponent, RouterOutlet],
})
export class LayoutComponent {
  data$: Observable<IComponentData> = this.getData();

  constructor(
    private readonly store: Store,
    private readonly headerAnimationService: HeaderService,
  ) {}

  onLogout(): void {
    this.store.dispatch(AUTH_ACTIONS.logout());
  }

  private getData(): Observable<IComponentData> {
    return combineLatest([
      this.store.select(AUTH_SELECTORS.userName),
      this.store.select(USER_PREFERENCES_REDUCER.isNavigationPinned),
      this.store.select(APPLICATION_SELECTORS.selectIsDesktop),
      this.headerAnimationService.headerHidden$,
      this.store.select(CLAIMS_SELECT.error),
      this.store.select(ACCOUNTS_SELECTORS.isError),
      this.store.select(SITES_SELECTORS.isError),
    ]).pipe(
      map(
        ([userName, isNavigationPinned, isDesktop, isHeaderHidden, isClaimsError, isAccountsError, isSitesError]) => ({
          userName,
          isHeaderHidden,
          isNavigationPinned: isDesktop && isNavigationPinned,
          isCriticalError: isAccountsError || isClaimsError || isSitesError,
        }),
      ),
    );
  }
}

interface IComponentData {
  userName: string;
  isNavigationPinned: boolean;
  isHeaderHidden: boolean;
  isCriticalError: boolean;
}
